'use client';

import { useMemo } from 'react';

import dynamic from 'next/dynamic';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import { useRoqLogicLibrary } from '@/components/Providers/RoqLogicLibraryProvider';
import { useManageBlogs } from '@/helpers/hooks/useManageBlogs';
import useWebAlias from '@/helpers/hooks/useWebalias';
import useWindowDimensions from '@/helpers/hooks/useWindowDimensions';
import { OpenUrlInNewTab } from '@/helpers/utils/common';
import { useAppContext } from '@/layouts/AppProvider';
import { RootState, useAppSelector } from '@/store';
import { CustomLink } from '@/types/Common';
import { MetaDataStrapi } from '@/types/MetaData';

// const DesktopMenu = dynamic(() => import('./DesktopMenu'));
import DesktopMenu from './DesktopMenu';

const MobileMenu = dynamic(() => import('./MobileMenu'));

interface MenuProps {
  // metaData: MetaDataStrapi;
}

const Menu = ({}: MenuProps) => {
  const { webalias, generateURL } = useWebAlias();
  const { isBackOffice } = useAppContext();
  const { width } = useWindowDimensions();
  const { handleLibraryClick } = useRoqLogicLibrary();
  const { handleManageBlogsClick } = useManageBlogs();
  const metaData: MetaDataStrapi = useAppSelector(
    (state: RootState) => state.metaDataApi.queries['getMetaData(undefined)']?.data as MetaDataStrapi
  );

  const { Dog, Cat } = metaData || {};

  const catIssues = Cat?.Issues || [];
  const dogIssues = Dog?.Issues || [];

  const catCategories = (Cat?.Categories ? [...Cat.Categories] : []).sort((a, b) => {
    // Convert both names to lowercase for case-insensitive sorting
    const nameA = a?.Name?.toLowerCase();
    const nameB = b?.Name.toLowerCase();

    if (a.id === Infinity || b.id === Infinity) return 0;

    // Compare the names
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // Names are equal
    return 0;
  });

  const dogCategories = (Dog?.Categories ? [...Dog.Categories] : []).sort((a, b) => {
    // Convert both names to lowercase for case-insensitive sorting
    const nameA = a?.Name?.toLowerCase();
    const nameB = b?.Name.toLowerCase();

    // Compare the names

    if (a.id === Infinity || b.id === Infinity) return 0;

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // Names are equal
    return 0;
  });

  const { customer } = useAuthContext();

  const ourStories = [
    {
      name: 'Pet Stories',
      link: isBackOffice
        ? `https://shop.pawtree.com/${customer?.webAlias}/testimonial`
        : generateURL('/testimonial', webalias),
    },
    {
      name: 'petPro Stories',
      link: isBackOffice
        ? `https://shop.pawtree.com/${customer?.webAlias}/testimonial/petpros`
        : generateURL('/testimonial/petpros', webalias),
    },
  ];

  const aboutUs = useMemo(() => {
    return [
      {
        name: 'Earn An Income',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/enrollment`
          : generateURL('/enrollment', webalias),
      },
      {
        name: 'pawTree Story',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/pawtree-story`
          : generateURL('/pawtree-story', webalias),
      },
      {
        name: 'Why pawTree',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/why-pawtree`
          : generateURL('/why-pawtree', webalias),
      },
      {
        name: 'Join Paw Club',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/paw-club`
          : generateURL('/paw-club', webalias),
      },
    ] as CustomLink[];
  }, [customer, generateURL, isBackOffice, webalias]);

  const businessReports = useMemo(
    () =>
      [
        { name: 'Volume Reports', link: generateURL('/volume-reports') },
        { name: 'Downline Orders Report', link: generateURL('/downline-orders') },
        // { name: 'Downline Title Report', link: generateURL('/downline-title') },
        { name: 'Fast Start Report', link: generateURL('/faststart-Report') },
        { name: 'Former petPro Report', link: generateURL('/formerPetpro-Report') },
        { name: 'MVR Caps Report', link: generateURL('/MVR-Caps-Report') },
        { name: 'petPro Report', link: generateURL('/Petpro-Report') },
        { name: 'Prospect List', link: generateURL('/Prospect-List') },
        { name: 'Qualification History Report', link: generateURL('/Qualification-History-Report') },
        // { name: 'Title Promotion Report', link: generateURL('/Title-Promotion-Report') },
        // { name: 'Top 30 Reco Report', link: generateURL('/Top-30-Reco-Report') },
        // { name: 'Title History Report', link: generateURL('/Title-History-Report') },
        { name: 'Title Reports', link: generateURL('/title-reports') },
        { name: 'Tree Viewer', link: generateURL('/tree-viewer') },
      ] as CustomLink[],
    [generateURL]
  );

  const customerReports = useMemo(
    () =>
      [
        { name: 'Cancelled EZ Ship Report', link: generateURL('/Cancellation-Report') },
        { name: 'Customer List', link: generateURL('/Customer-List') },
        { name: 'Customer History', link: generateURL('/Customer-History') },
        { name: 'Pet Quiz Report', link: generateURL('/organization/petprofilereport') },
        { name: 'Product Sales Report', link: generateURL('/Product-Sales-Report') },
        { name: 'Lead Generation', link: generateURL('/lead-generation-report') },

        // ...(customer && isPuptimusPrime(customer)
        //   ? [{ name: 'Abandoned Cart Report', link: generateURL('/abandoned-cart-report') }]
        //   : []),
      ] as CustomLink[],
    [generateURL, customer]
  );

  const commissions = useMemo(
    () =>
      [
        { name: 'Commissions', link: generateURL('/commissions') },
        { name: 'Monthly Commission Earnings', link: generateURL('/volumes') },
        {
          name: 'PawPay',
          link: 'https://pawpay.mypayquicker.com',
          target: '_blank',
          // onClick: link => OpenUrlInNewTab(link),
        },
      ] as CustomLink[],
    [generateURL]
  );

  const events = useMemo(
    () =>
      [
        { name: 'Calendar', link: generateURL('/events/calendar') },
        { name: 'Subscriptions', link: generateURL('/events/calendar/subscriptions') },

        {
          name: 'Legacy',
          link: 'https://escadadesign.wixsite.com/pawtree-legacy-2026',
          target: '_blank',
          // onClick: link => OpenUrlInNewTab(link),
        },
        {
          name: 'Pawfest',
          link: 'https://pawtreepawfest.com',
          target: '_blank',
          onClick: () => OpenUrlInNewTab('https://pawtreepawfest.com'),
        },
        {
          name: 'Pawtree blog',
          link: `https://blog.pawtree.com/${
            isBackOffice ? customer?.webAlias || customer?.company || webalias : webalias
          }`,
          target: '_blank',
          // onClick: link => OpenUrlInNewTab(link),
        },
      ] as CustomLink[],
    [generateURL, customer, isBackOffice, webalias]
  );

  const proTools = useMemo(
    () =>
      [
        // {
        //   name: 'The pawTree Greenhouse',
        //   link: 'https://www.thepawtreegreenhouse.com',
        //   target: '_blank',
        //   onClick: link => OpenUrlInNewTab('https://www.thepawtreegreenhouse.com'),
        // },
        {
          name: 'Pawtree University',
          link: 'https://pawtreeuniversity.com',
          target: '_blank',
          onClick: link => OpenUrlInNewTab('https://pawtreeuniversity.com'),
        },
        { name: 'Library', link: '#', as: 'a', onClick: handleLibraryClick },
        { name: 'Proshop', link: generateURL('/shopping/products/557'), as: 'Link' },
        { name: 'Fund A Gift Card', link: generateURL('/giftcards/fundgiftcard') },
        // { name: 'Vista Print', link: generateURL('/MaintenancePage') },
        {
          name: 'Enroll a petPro',
          link: `https://shop.pawtree.com/${customer?.webAlias}/enrollment`,
          target: '_blank',
          // onClick: link => OpenUrlInNewTab(link),
        },
      ] as CustomLink[],
    [generateURL, handleLibraryClick]
  );

  const support = useMemo(
    () =>
      [
        { name: 'Help Desk', link: 'mailto:support@pawtree.com' },
        {
          name: 'petPro Faq',
          link: 'https://pawtree.zendesk.com/hc/en-us',
          target: '_blank',
          // onClick: link => OpenUrlInNewTab(link),
        },
        // {
        //   name: 'SOLUTIONS GUIDE FAQ',
        //   link: 'https://pawtree.zendesk.com/hc/en-us/articles/360049598733',
        //   target: '_blank',
        //   onClick: link => OpenUrlInNewTab(link),
        // },
      ] as CustomLink[],
    []
  );

  const tools = useMemo(
    () =>
      [
        // { name: 'Coupons', link: generateURL('/coupon') },
        // { name: 'Manage Pawtrip', link: generateURL('/pawtrip/managepawtrip') },
        // { name: 'Manage Pawtrip Points', link: generateURL('/pawtrip/managepawtrippoints') },
        // { name: 'Pawtirp Calc', link: generateURL('/pawtrip/pawtirpcalcadmin') },
        // { name: 'Pawtrip Admin Report', link: generateURL('/pawtrip/pawtripadminreport') },
        // { name: 'Pawtrip Totals Admin Report', link: generateURL('/pawtrip/pawtriptotalsadminreport') },
        // { name: 'Dallas Warehouse 1', link: generateURL('/admin/shippingfile') },
        { name: 'Manage Gift Cards', link: generateURL('/giftcards/managegiftcards') },
        // { name: 'Manage Resources', link: generateURL('/resources/manageresources') },
        // { name: 'Manage Testimonials', link: generateURL('/testimonials/managetestimonials') },
        // { name: 'Manage petPro Testimonials', link: generateURL('/testimonials/managepetprotestimonials') },
        { name: 'Pick A Name', link: generateURL('/randomizer') },
        // { name: 'MANAGE MONTHLY ITEM', link: generateURL('/monthlyitem') },
        // { name: 'Manage Trypawtree', link: generateURL('/trypawtree') },
        // { name: 'Cache Manager', link: generateURL('/admin/cachemanager') },
        {
          name: 'Manage Blogs',
          link: generateURL('/blogs'),
          as: 'a',
          onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) =>
            handleManageBlogsClick(e, generateURL('/blogs')),
        },
        {
          name: 'Manage Blog Images',
          link: generateURL('/blogs/blogimages'),
          as: 'a',
          onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) =>
            handleManageBlogsClick(e, generateURL('/blogs/blogimages')),
        },
        { name: 'Giftcard Transactions Report', link: generateURL('/giftcard-transactions-report') },
        { name: 'Funded Giftcard Report', link: generateURL('/funded-giftcard-transactions-report') },
        { name: 'Admin GC Transactions Report', link: generateURL('/admin-giftcard-transactions-report') },
        // { name: 'Manage Extended Items', link: generateURL('/manage-extended-items') },
        // { name: 'Manage Item Inventory', link: generateURL('/manage-item-inventory') },
        // { name: 'Abandoned Cart Report', link: generateURL('/abandoned-cart-report') },
        // { name: 'Core 4 Admin Report', link: generateURL('/core-4-admin-report') },
        { name: 'Manage Breed', link: generateURL('/admin/managebreed') },
      ] as CustomLink[],
    [generateURL, customer, handleManageBlogsClick]
  );

  const petProfileAdmin = useMemo(
    () =>
      [
        // { name: 'Manage Food Store', link: generateURL('/admin/managefoodstore') },
        // { name: 'Other Food', link: generateURL('/admin/manageotherfoods') },
        // { name: 'Recipe Recommendations', link: generateURL('/admin/reciperecommendation') },
        // { name: 'Pet Quiz', link: generateURL('/admin/petprofile') },
        { name: 'Labels Report', link: generateURL('/petprofile/labelreport') },
      ] as CustomLink[],
    [generateURL]
  );

  const petProfilePath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias ?? ''}/petprofile`
    : !customer?.customerID
    ? generateURL('/petprofile/Welcome', webalias)
    : generateURL('/petprofile', webalias);
  const ezShipManagerPath = isBackOffice ? '/AutoOrders/auto-order-list' : '/auto-order/auto-order-list';

  const userSettings = useMemo(() => {
    const settings = [
      {
        name: 'Account Settings',
        link: isBackOffice ? '/account/settings' : generateURL('/account/settings', webalias),
      },
      { name: 'Orders', link: isBackOffice ? '/orders' : generateURL('/orders', webalias) },
      {
        name: 'EZ Ships',
        link: isBackOffice ? '/AutoOrders/auto-order-list' : generateURL('/auto-order/auto-order-list', webalias),
      },
      { name: 'Addresses', link: isBackOffice ? '/account/addresses' : generateURL('/account/addresses', webalias) },

      // { name: 'Sign out', link: generateURL('/account/logout') },
    ] as CustomLink[];

    if (isBackOffice) {
      settings.push({ name: 'Avatar', link: generateURL('/account/avatar') });
      settings.push({ name: 'Point Accounts', link: generateURL('/account/earnings-account') });
      settings.push({ name: 'Notifications', link: generateURL('/account/notifications') });
    } else {
      settings.push({ name: 'Payment Methods', link: generateURL('/account/paymentmethods', webalias) });
      settings.push({ name: 'Fund A Gift Card', link: generateURL('/giftcards/fundgiftcard', webalias) });
    }

    // settings.push({ name: 'Manage Custom Food Labels', link: generateURL('/foodlabel/managelabels') });

    return settings;
  }, [generateURL, isBackOffice, webalias]);

  const dashboardUrl = '/';
  const blogUrl = `https://blog.pawtree.com/${isBackOffice ? customer?.webAlias || customer?.company || '' : webalias}`;
  const ingredientUrl = generateURL('/ingredient-glossary', webalias);

  return (
    <>
      {width <= 1023 && (
        <MobileMenu
          aboutUs={aboutUs}
          blogUrl={blogUrl}
          ingredientUrl={ingredientUrl}
          catCategories={catCategories}
          catIssues={catIssues}
          dogCategories={dogCategories}
          dogIssues={dogIssues}
          ezShipManagerPath={ezShipManagerPath}
          ourStories={ourStories}
          petProfilePath={petProfilePath}
          userSettings={userSettings}
          businessReports={businessReports}
          commissions={commissions}
          customerReports={customerReports}
          dashboardUrl={dashboardUrl}
          events={events}
          petProfileAdmin={petProfileAdmin}
          proTools={proTools as CustomLink[]}
          support={support}
          tools={tools}
        />
      )}
      <DesktopMenu
        aboutUs={aboutUs}
        blogUrl={blogUrl}
        ingredientUrl={ingredientUrl}
        catCategories={catCategories}
        catIssues={catIssues}
        dogCategories={dogCategories}
        dogIssues={dogIssues}
        ezShipManagerPath={ezShipManagerPath}
        ourStories={ourStories}
        petProfilePath={petProfilePath}
        businessReports={businessReports}
        commissions={commissions}
        customerReports={customerReports}
        dashboardUrl={dashboardUrl}
        events={events}
        petProfileAdmin={petProfileAdmin}
        proTools={proTools as CustomLink[]}
        support={support}
        tools={tools}
      />
    </>
  );
};

export default Menu;
